import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { SESSION_KEY } from './session.constants';
import { NavigationService } from './navigation.service';
import { isPlatformBrowser } from '@angular/common';

@Injectable({
  providedIn: 'root',
})
export class SessionService {
  private isBrowser: any = isPlatformBrowser(inject(PLATFORM_ID));
  private storageKey = 'vmo2_zeus_session';
  private navigationService = inject(NavigationService);

  private getStoredData(): { [key: string]: { any } } {
    let storedData = {};
    if (this.isBrowser) {
      const storedDataString = sessionStorage.getItem(this.storageKey);
      storedData = storedDataString ? JSON.parse(storedDataString) : {};
      storedData[SESSION_KEY.LOCATION] = this.getLocationData();
    }
    return storedData;
  }

  private setStoredData(data: { [key: string]: any }): void {
    if (this.isBrowser) {
      sessionStorage.setItem(this.storageKey, JSON.stringify(data));
    }
  }

  setData(key: SESSION_KEY, data: any): void {
    const storedData = this.getStoredData();
    storedData[key] = data;
    this.setStoredData(storedData);
  }

  getLocationData() {
    return {
      isEmbeddedWebApp: this.navigationService.isEmbeddedWebApp(),
      queryString: this.isBrowser ? window?.location?.search : '',
      referrer: this.isBrowser ? window?.document?.referrer : '',
    };
  }

  getData(key: SESSION_KEY | null = null): any {
    const storedData = this.getStoredData();
    if (!key) {
      return storedData; // Return the entire stored data JSON
    } else if (storedData[key]) {
      return storedData[key];
    } else {
      return null;
    }
  }

  removeData(key: SESSION_KEY | null = null): void {
    if (key) {
      const storedData = this.getStoredData();
      delete storedData[key];
      this.setStoredData(storedData);
    } else {
      sessionStorage.removeItem(this.storageKey);
    }
  }

  getDiagnosticActionCode(data, isSecondaryCode = false): string {
    let result = null;
    if (data?.deviceDetails?.length > 0 && data?.deviceDetails[0]?.actionCodes?.length > 0) {
      const actionCodes = isSecondaryCode ? data?.deviceDetails[0]?.actionCodes[1] : data?.deviceDetails[0]?.actionCodes[0];
      result = actionCodes.actionCode?.toLocaleLowerCase();
    }
    return result;
  }

  getNextAppointment(data = null): any {
    const appointmentWorkOrderStageCodes = ['PD', 'OD', 'OW', 'EL', 'MM', 'GO'];
    const now = new Date();
    let nextWorkOrder = null;

    if (!data) {
      data = this.getData(SESSION_KEY.ACCOUNT_CHECK);
    }

    if (data?.pendingOrders?.hasOpenScheduledFaultOrder === true) {
      // get a list of valid appointments.
      const workOrders = data?.pendingOrders?.orderDetails?.filter((item) => {
        if (
          item?.workOrderType === 'TC' &&
          item?.workOrderScheduleDate &&
          item?.workOrderStartTime &&
          new Date(`${item?.workOrderScheduleDate}T${item?.workOrderStartTime}`) > now &&
          (!item.workOrderStageCode || appointmentWorkOrderStageCodes.includes(item.workOrderStageCode))
        ) {
          return item;
        }
      });

      if (workOrders?.length == 1) {
        nextWorkOrder = workOrders[0];
      } else if (workOrders?.length > 1) {
        // if more than one valid appointment get the next
        nextWorkOrder = workOrders[0];
        let nextWorkOrderDateTime = new Date(
          `${workOrders[0].workOrderScheduleDate}T${workOrders[0]?.workOrderStartTime}`
        );
        for (let i = 1; i < workOrders.length; i++) {
          const currentWorkOrderDateTime = new Date(
            `${workOrders[i].workOrderScheduleDate}T${workOrders[i]?.workOrderStartTime}`
          );
          if (currentWorkOrderDateTime < nextWorkOrderDateTime) {
            nextWorkOrder = workOrders[i];
            nextWorkOrderDateTime = currentWorkOrderDateTime;
          }
        }
      }
    }

    return nextWorkOrder;
  }
}
